import { Task } from "@/types/User";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/Button";
import { Link } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";

const SurveyTask = ({
  leaseId,
  task,
  propertyName,
}: {
  leaseId: number;
  task: Task;
  propertyName: string;
}) => {
  const surveyId = task?.metaData?.surveyId as number | undefined;
  const title = (task?.metaData?.title as string | undefined) ?? "New survey";
  const description = task?.metaData?.description as string | undefined;
  const storageKey = useMemo(
    () => `close-survey-${surveyId ?? title}`,
    [surveyId, title]
  );
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(!sessionStorage.getItem(storageKey));
  }, [storageKey]);

  if (!open || !surveyId) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        className="p-3.5 bg-white rounded-xl shadow-md mb-4"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
      >
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="text-[#F67F74]">
                <FontAwesomeIcon icon="clipboard-question" className="text-lg" />
              </div>
              <div className="flex-1">
                <p className="text-lg font-medium leading-none text-[#202628]">
                  Survey
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                sessionStorage.setItem(storageKey, "true");
              }}
              className="cursor-pointer"
              aria-label="Dismiss survey alert"
            >
              <FontAwesomeIcon icon="times" className="text-lg text-[#A7A9AC]" />
            </button>
          </div>

          <div className="mt-2.5">
            <p className="text-xl leading-tight font-normal text-[#202628] break-words">
              {title}
            </p>
          </div>
          <div className="mt-2">
            <p className="text-sm leading-snug text-[#202628] break-words">
              {description?.trim() || "Share your feedback about your community"}
            </p>
          </div>

          <Link
            to={`/lease/${leaseId}/surveys/${surveyId}`}
            className="block w-full mt-3"
          >
            <Button color="secondary" className="w-full h-10 text-sm font-semibold">
              Take Survey
            </Button>
          </Link>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SurveyTask;
